/* eslint-disable @typescript-eslint/no-require-imports */
import { setAccountingLang } from "@bokio/mobile-web-shared/core/lang/AccountingLangFactory";
import { setBackOfficeLang } from "@bokio/mobile-web-shared/core/lang/BackOfficeLangFactory";
import { setBankLang } from "@bokio/mobile-web-shared/core/lang/BankLangFactory";
import { setClosuresLang } from "@bokio/mobile-web-shared/core/lang/ClosuresLangFactory";
import { setCountriesLang } from "@bokio/mobile-web-shared/core/lang/CountriesLangFactory";
import { setExpensesLang } from "@bokio/mobile-web-shared/core/lang/ExpensesLangFactory";
import { setGeneralLang } from "@bokio/mobile-web-shared/core/lang/GeneralLangFactory";
import { setInvoicesLang } from "@bokio/mobile-web-shared/core/lang/InvoicesLangFactory";
import { setOnboardingLang } from "@bokio/mobile-web-shared/core/lang/OnboardingLangFactory";
import { setPartnersLang } from "@bokio/mobile-web-shared/core/lang/PartnersLangFactory";
import { setPayslipCalculationsLang } from "@bokio/mobile-web-shared/core/lang/PayslipCalculationsLangFactory";
import { setRecipesLang } from "@bokio/mobile-web-shared/core/lang/RecipesLangFactory";
import { setSalaryLang } from "@bokio/mobile-web-shared/core/lang/SalaryLangFactory";
import { setSettingsLang } from "@bokio/mobile-web-shared/core/lang/SettingsLangFactory";
import { setSignUpLang } from "@bokio/mobile-web-shared/core/lang/SignUpLangFactory";
import { setTranslateTempLang } from "@bokio/mobile-web-shared/core/lang/TranslatableLanguage";
import { Config } from "@bokio/shared/config";

/*
 * Language handling hack. This is to be able to update each language without creating a dependency on all of them that will load the whole tree.
 */
export type LangListener = (lang: string) => void;

class LanguageNotifier {
	listeners: Set<LangListener> = new Set();
	currentLang = "";
	private previousLang = "";
	private isInDebugMode = false;

	private loadAreaLanguages = async (lang: string) => {
		this.currentLang = lang;

		setTranslateTempLang(lang);
		const data = await Promise.all([
			setAccountingLang(lang),
			setClosuresLang(lang),
			setExpensesLang(lang),
			setGeneralLang(lang),
			setInvoicesLang(lang),
			setPartnersLang(lang),
			setPayslipCalculationsLang(lang),
			setRecipesLang(lang),
			setSalaryLang(lang),
			setSettingsLang(lang),
			setSignUpLang(lang),
			setOnboardingLang(lang),
			setCountriesLang(lang),
			setBackOfficeLang(lang),
			setBankLang(lang),
		]);
		Config.setup({ lang });
		this.listeners.forEach(listener => listener(lang));

		return data;
	};

	setDebugLang = () => {
		this.previousLang = this.currentLang;
		this.isInDebugMode = true;
		// This is Kyrgyzstan, we need to use a real culture because the time formats will be messed up otherwise.
		// Kyrgyzstan is random and can be changed, it was chosen because it's a language that we won't likely support
		// If you change this you need to look for all places this string is used.
		this.loadAreaLanguages("ky-KG");
	};

	clearDebugLang = () => {
		this.isInDebugMode = false;
		this.loadAreaLanguages(this.previousLang);
	};

	load = async (lang: string) => {
		if (this.isInDebugMode) {
			// eslint-disable-next-line no-console
			console.warn(
				"The languageNotifier is in debug Mode, to switch back from debug mode, run `window.clearDebugLang()` in your console or refresh the window.",
			);
			return;
		}

		return this.loadAreaLanguages(lang);
	};

	subscribe = (item: LangListener) => {
		this.listeners.add(item);
		return () => {
			this.listeners.delete(item);
		};
	};
}

export const languageNotifier = new LanguageNotifier();

if (module.hot) {
	module.hot.accept(
		[
			require.resolve("@bokio/mobile-web-shared/core/lang/AccountingLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/BackOfficeLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/BankLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/ClosuresLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/CountriesLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/ExpensesLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/GeneralLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/InvoicesLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/OnboardingLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/PartnersLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/PayslipCalculationsLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/RecipesLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/SalaryLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/SettingsLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/SignUpLangFactory"),
			require.resolve("@bokio/mobile-web-shared/core/lang/TranslatableLanguage"),
		],
		() => {
			const newSetAccountingLang =
				require("@bokio/mobile-web-shared/core/lang/AccountingLangFactory").setAccountingLang;
			const newSetBackOfficeLang =
				require("@bokio/mobile-web-shared/core/lang/BackOfficeLangFactory").setBackOfficeLang;
			const newSetBankLang = require("@bokio/mobile-web-shared/core/lang/BankLangFactory").setBankLang;
			const newSetClosuresLang = require("@bokio/mobile-web-shared/core/lang/ClosuresLangFactory").setClosuresLang;
			const newSetCountriesLang = require("@bokio/mobile-web-shared/core/lang/CountriesLangFactory").setCountriesLang;
			const newSetExpensesLang = require("@bokio/mobile-web-shared/core/lang/ExpensesLangFactory").setExpensesLang;
			const newSetGeneralLang = require("@bokio/mobile-web-shared/core/lang/GeneralLangFactory").setGeneralLang;
			const newSetInvoicesLang = require("@bokio/mobile-web-shared/core/lang/InvoicesLangFactory").setInvoicesLang;
			const newSetOnboardingLang =
				require("@bokio/mobile-web-shared/core/lang/OnboardingLangFactory").setOnboardingLang;
			const newSetPartnersLang = require("@bokio/mobile-web-shared/core/lang/PartnersLangFactory").setPartnersLang;
			const newSetPayslipCalculationsLang =
				require("@bokio/mobile-web-shared/core/lang/PayslipCalculationsLangFactory").setPayslipCalculationsLang;
			const newSetRecipesLang = require("@bokio/mobile-web-shared/core/lang/RecipesLangFactory").setRecipesLang;
			const newSetSalaryLang = require("@bokio/mobile-web-shared/core/lang/SalaryLangFactory").setSalaryLang;
			const newSetSettingsLang = require("@bokio/mobile-web-shared/core/lang/SettingsLangFactory").setSettingsLang;
			const newSetSignUpLang = require("@bokio/mobile-web-shared/core/lang/SignUpLangFactory").setSignUpLang;
			const newSetTranslateTempLang =
				require("@bokio/mobile-web-shared/core/lang/TranslatableLanguage").setTranslateTempLang;
			// update all areas
			newSetTranslateTempLang(languageNotifier.currentLang);
			newSetAccountingLang(languageNotifier.currentLang);
			newSetClosuresLang(languageNotifier.currentLang);
			newSetExpensesLang(languageNotifier.currentLang);
			newSetGeneralLang(languageNotifier.currentLang);
			newSetInvoicesLang(languageNotifier.currentLang);
			newSetPartnersLang(languageNotifier.currentLang);
			newSetPayslipCalculationsLang(languageNotifier.currentLang);
			newSetRecipesLang(languageNotifier.currentLang);
			newSetSalaryLang(languageNotifier.currentLang);
			newSetSettingsLang(languageNotifier.currentLang);
			newSetSignUpLang(languageNotifier.currentLang);
			newSetOnboardingLang(languageNotifier.currentLang);
			newSetCountriesLang(languageNotifier.currentLang);
			newSetBackOfficeLang(languageNotifier.currentLang);
			newSetBankLang(languageNotifier.currentLang);
			// re-load so that any places that subscribe gets updated
			languageNotifier.load(languageNotifier.currentLang);
		},
	);
}
